import type { Sector } from '@chicare/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

/**
 * Pharmacy onboarding, approval, and listings.
 *
 * Every one of these is worked rather than read, so nothing may be stale. A
 * pharmacist who uploads a document must see the checklist shrink; a reviewer
 * who approves a licence must see the approve button unlock. Both are
 * invalidations, not refetch timers.
 */

export const onboardingKeys = {
  pharmacy: () => ['seller', 'pharmacy'] as const,
  listings: () => ['seller', 'listings'] as const,
  listing: (id: number) => ['seller', 'listings', id] as const,
  approvals: (status: string, query: string) =>
    ['admin', 'pharmacies', status, query] as const,
  approval: (id: number) => ['admin', 'pharmacies', 'detail', id] as const,
};

// ---------------------------------------------------------------------------
// Seller onboarding
// ---------------------------------------------------------------------------

export function useSellerPharmacy() {
  return useQuery({
    queryKey: onboardingKeys.pharmacy(),
    queryFn: ({ signal }) => api.sellerPharmacy(signal),
    staleTime: 0,
  });
}

export function useRegisterPharmacy() {
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.registerPharmacy(input),
  });
}

/**
 * Sending in a renewed licence.
 *
 * Separate from `useUpdatePharmacy` because the underlying endpoint is: the
 * registration freezes when a pharmacy goes live, and a renewal has to reach
 * past that freeze for exactly one field and nothing else.
 */
export function useSubmitLicenceRenewal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      sector?: Sector;
      licence_no: string;
      expiry_date: string;
      file: File;
    }) => api.submitLicenceRenewal(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seller'] }),
  });
}

export function useUpdatePharmacy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.updateSellerPharmacy(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seller'] }),
  });
}

export function useUploadPharmacyDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ type, file }: { type: string; file: File }) =>
      api.uploadPharmacyDocument(type, file),
    // The checklist must shrink the moment a document lands, or a pharmacist
    // uploads the same licence three times wondering why nothing changed.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seller'] }),
  });
}

export function useSubmitPharmacy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.submitPharmacy(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seller'] }),
  });
}

// ---------------------------------------------------------------------------
// Admin approval
// ---------------------------------------------------------------------------

/**
 * The admin register of people.
 *
 * Kept fresh: an operator on the phone to somebody who has just signed up
 * needs to see them, not a cached list from before the call started.
 */
export function useAdminUsers(query = '', role = 'all') {
  return useQuery({
    queryKey: ['admin', 'users', query, role] as const,
    queryFn: ({ signal }) => api.adminUsers({ q: query || undefined, role }, signal),
    staleTime: 0,
    placeholderData: (previous) => previous,
  });
}

/**
 * Licence renewals waiting to be read.
 *
 * Polled a little more eagerly than the registration queue: every hour one of
 * these sits here is an hour a real pharmacy has nothing on sale.
 */
export function useLicenceRenewals(status = 'pending') {
  return useQuery({
    queryKey: ['admin', 'licence-renewals', status],
    queryFn: ({ signal }) => api.licenceRenewals(status, signal),
    refetchInterval: 60_000,
  });
}

export function useDecideLicenceRenewal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input:
      | { action: 'approve'; pharmacyId: number; renewalId: number; reason: string }
      | { action: 'reject'; pharmacyId: number; renewalId: number; note: string }) =>
      input.action === 'approve'
        ? api.approveLicenceRenewal(input.pharmacyId, input.renewalId, input.reason)
        : api.rejectLicenceRenewal(input.pharmacyId, input.renewalId, input.note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'licence-renewals'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'pharmacies'] });
    },
  });
}

export function usePharmacyApprovals(status = 'pending_approval', query = '') {
  return useQuery({
    queryKey: onboardingKeys.approvals(status, query),
    queryFn: ({ signal }) => api.pharmacyApprovals(status, query, signal),
    staleTime: 0,
    // Keeps the previous rows on screen while a new search resolves, so the
    // list does not blink to empty on every keystroke.
    placeholderData: (previous) => previous,
  });
}

export function usePharmacyApproval(id: number | null) {
  return useQuery({
    queryKey: onboardingKeys.approval(id ?? 0),
    queryFn: ({ signal }) => api.pharmacyApproval(id as number, signal),
    enabled: id !== null,
    staleTime: 0,
  });
}

export function useReviewDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      pharmacyId,
      documentId,
      status,
      note,
    }: {
      pharmacyId: number;
      documentId: number;
      status: 'approved' | 'rejected';
      note?: string;
    }) => api.reviewPharmacyDocument(pharmacyId, documentId, { status, note }),
    // Reading the last document is what unlocks approval, so the blockers have
    // to refresh with it.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'pharmacies'] }),
  });
}

export function useDecidePharmacy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      decision,
      reason,
    }: {
      id: number;
      decision: 'approve' | 'reject';
      reason: string;
    }) =>
      decision === 'approve' ? api.approvePharmacy(id, reason) : api.rejectPharmacy(id, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'pharmacies'] }),
  });
}

// ---------------------------------------------------------------------------
// Listings & stock
// ---------------------------------------------------------------------------

export function useSellerListings() {
  return useQuery({
    queryKey: onboardingKeys.listings(),
    queryFn: ({ signal }) => api.sellerListings(signal),
    staleTime: 0,
  });
}

export function useSellerListing(id: number | null) {
  return useQuery({
    queryKey: onboardingKeys.listing(id ?? 0),
    queryFn: ({ signal }) => api.sellerListing(id as number, signal),
    enabled: id !== null,
    staleTime: 0,
  });
}

export function useCreateListing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { productId: number; priceKobo: number; reorderLevel?: number }) =>
      api.createListing(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seller', 'listings'] }),
  });
}

export function useUpdateListing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: number;
      priceKobo?: number;
      reorderLevel?: number;
      isActive?: boolean;
    }) => api.updateListing(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seller', 'listings'] }),
  });
}

export function useAddBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      listingId,
      ...input
    }: {
      listingId: number;
      batchNo: string;
      expiryDate: string;
      quantity: number;
    }) => api.addBatch(listingId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seller', 'listings'] }),
  });
}

export function useAdjustBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      batchId,
      quantity,
      reason,
    }: {
      batchId: number;
      quantity: number;
      reason: string;
    }) => api.adjustBatch(batchId, quantity, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seller', 'listings'] }),
  });
}

/** Finding a product to list. Reuses the public catalogue search. */
export function useProductSearch(query: string) {
  return useQuery({
    queryKey: ['seller', 'product-search', query],
    queryFn: ({ signal }) => api.searchProducts({ q: query, per_page: 12 }, signal),
    enabled: query.trim().length >= 2,
    staleTime: 60_000,
  });
}
